import { useState, useEffect, useCallback } from 'react';
import type { Person } from '../types/timesheet.types';
import { PersonRole } from '../types/timesheet.types';
import { useConnector } from '../contexts/ConnectorContext';
import { errorLogger } from '../utils/errorLogger';

interface CurrentUserState {
  person: Person | null;
  isLoading: boolean;
  error: Error | null;
}

interface CurrentUserResult {
  person: Person;
  isLoading: boolean;
  error: Error | null;
  canApprove: boolean;
  isSuperApprover: boolean;
  isUnlockGroup: boolean;
  isAdmin: boolean;
  canViewAssetTimesheets: boolean;
  canViewBillOutRates: boolean;
  reload: () => void;
}

export function useCurrentUser(): CurrentUserResult {
  const connector = useConnector();
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const reload = useCallback(() => setReloadTrigger(t => t + 1), []);

  const [state, setState] = useState<CurrentUserState>({
    person: null,
    isLoading: true,
    error: null,
  });
  const [canViewAssetTimesheets, setCanViewAssetTimesheets] = useState(false);
  const [canViewBillOutRates, setCanViewBillOutRates] = useState(false);
  const [isUnlockGroup, setIsUnlockGroup] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [isSuperApprover, setIsSuperApprover] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const isBackgroundRefresh = reloadTrigger > 0;
    connector
      .getCurrentUser()
      .then(person => {
        if (!cancelled) setState({ person, isLoading: false, error: null });
      })
      .catch(err => {
        if (!cancelled && !isBackgroundRefresh) {
          const error = err instanceof Error ? err : new Error(String(err));
          errorLogger.log(error, { component: 'useCurrentUser', action: 'getCurrentUser' });
          setState({ person: null, isLoading: false, error });
        }
      });

    return () => { cancelled = true; };
  }, [connector, reloadTrigger]);

  useEffect(() => {
    const person = state.person;
    if (!person) { setCanApprove(false); return; }
    let cancelled = false;
    const email = person.m365Email;
    Promise.all([connector.getDirectReports(email), connector.getBackupReports(email)])
      .then(([directs, backups]) => {
        if (!cancelled) setCanApprove(directs.length > 0 || backups.length > 0);
      })
      .catch(() => { if (!cancelled) setCanApprove(false); });
    return () => { cancelled = true; };
  }, [connector, state.person]);

  useEffect(() => {
    const person = state.person;
    if (!person) { setIsSuperApprover(false); return; }
    if (person.role === PersonRole.PMOLead) { setIsSuperApprover(true); return; }
    let cancelled = false;
    connector.getSuperApproverReports(person.m365Email)
      .then(reports => {
        if (!cancelled) setIsSuperApprover(reports.length > 0);
      })
      .catch(() => { if (!cancelled) setIsSuperApprover(false); });
    return () => { cancelled = true; };
  }, [connector, state.person]);

  useEffect(() => {
    const person = state.person;
    if (!person) { setIsUnlockGroup(false); return; }
    let cancelled = false;
    const email = person.m365Email.trim().toLowerCase();
    connector.getUnlockPermittedMembers()
      .then(permitted => { if (!cancelled) setIsUnlockGroup(permitted.has(email)); })
      .catch(() => { if (!cancelled) setIsUnlockGroup(false); });
    return () => { cancelled = true; };
  }, [connector, state.person]);

  // Determine asset timesheets access: unlock group and PMO Lead get it immediately;
  // others need to be checked against the Assets responsible person field or the
  // "Asset Timesheet Permitted Group" SharePoint list.
  useEffect(() => {
    const person = state.person;
    if (!person) {
      setCanViewAssetTimesheets(false);
      return;
    }
    if (person.isTimesheetMgmtGroup || person.role === PersonRole.PMOLead) {
      setCanViewAssetTimesheets(true);
      return;
    }
    let cancelled = false;
    const email = person.m365Email.trim().toLowerCase();
    Promise.all([connector.getAssets(), connector.getAssetPermittedMembers()])
      .then(([assets, permittedEmails]) => {
        if (cancelled) return;
        const isPermitted = permittedEmails.has(email);
        const isResponsible = assets.some(
          a => a.active && a.responsiblePeopleEmails.some(e => e.trim().toLowerCase() === email)
        );
        setCanViewAssetTimesheets(isPermitted || isResponsible);
      })
      .catch(() => { if (!cancelled) setCanViewAssetTimesheets(false); });
    return () => { cancelled = true; };
  }, [connector, state.person]);

  useEffect(() => {
    const person = state.person;
    if (!person) { setCanViewBillOutRates(false); return; }
    let cancelled = false;
    const email = person.m365Email.trim().toLowerCase();
    connector.getBillOutRatesPermittedMembers()
      .then(permitted => {
        //const permittedList = [...permitted].join(', ') || '(empty)';
        //alert(`[BillOutRates debug]\nLogged-in email: "${email}"\nPermitted emails: ${permittedList}\nHas access: ${permitted.has(email)}`);
        if (!cancelled) setCanViewBillOutRates(permitted.has(email));
      })
      .catch(() => { if (!cancelled) setCanViewBillOutRates(false); });
    return () => { cancelled = true; };
  }, [connector, state.person]);

  const person = state.person;//alert(person?.isTimesheetMgmtGroup);
  return {
    person: person as Person,
    isLoading: state.isLoading,
    error: state.error,
    canApprove,
    isSuperApprover,
    isUnlockGroup,
    isAdmin: person !== null && (person.role === PersonRole.SystemAdministrator || person.role === PersonRole.PMOLead),
    canViewAssetTimesheets,
    canViewBillOutRates,
    reload,
  };
}
